import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { fetchWallModels, type WallModel } from "@/lib/featuredWall";

const linkForModel = (m: WallModel) =>
  m.public_handle_enabled && m.public_handle ? `/@${m.public_handle}` : `/profile/${m.id}`;

export default function LiveNetworkSections() {
  const [models, setModels] = useState<WallModel[]>([]);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    (async () => {
      const { models: wall } = await fetchWallModels(24);
      setModels(wall);
    })();
  }, []);

  const allWallModels = models.slice(0, 24);
  // Mobile keeps the wall short - 8 profiles, then an explicit "view more".
  const mobileLimit = 8;
  const wallModels =
    isMobile && !expanded ? allWallModels.slice(0, mobileLimit) : allWallModels;
  const hasMoreOnMobile = isMobile && !expanded && allWallModels.length > mobileLimit;

  return (
    <>
      {/* ───────────────── TALENT WALL ───────────────── */}
      {wallModels.length > 0 && (
        <section className="border-b border-border bg-muted/20">
          <div className="container mx-auto px-6 lg:px-16 py-24">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">The network</p>
                <h2 className="font-display text-4xl md:text-5xl leading-tight font-light">
                  Real talent, live <em className="italic">right now</em>.
                </h2>
              </div>
              <Link
                to="/browse"
                className="text-sm font-medium underline underline-offset-4 hover:text-primary transition-colors"
              >
                See all talent →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">

              {wallModels.map((m) => (
                <Link
                  key={m.id}
                  to={linkForModel(m)}
                  className="group relative aspect-[3/4] overflow-hidden bg-muted rounded-md"
                >
                  {m.profile_image_url ? (
                    <img
                      src={m.profile_image_url}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                      {m.name}
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs font-medium truncate">{m.name}</p>
                    {m.agency_name && (
                      <p className="text-white/70 text-2xs truncate">{m.agency_name}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            {hasMoreOnMobile && (
              <div className="mt-6 sm:hidden">
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="w-full min-h-11 rounded-full border border-border bg-background text-sm font-medium py-3 hover:bg-muted transition-colors"
                >
                  View more talent
                </button>
              </div>
            )}

          </div>
        </section>
      )}
    </>
  );
}
